import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useQuery } from "@tanstack/react-query";
import { tournamentDayApi } from "@bw/shared/api";
import type { Schema } from "@bw/shared/types/generated";
import { useEffect, useMemo, useRef, useState } from "react";

import * as tournamentApi from "@/libs/tournamentApi";

type ScheduleItem = Schema["TournamentOperatorSchedulePage"]["items"][number];

function matchKey(item: ScheduleItem): string {
  return `${item.match.kind}:${item.match.match_id}`;
}

function errorMessage(): string {
  return "Game-sheet package could not be generated. Refresh the schedule and try again.";
}

/**
 * Selects server-owned current-plan schedule rows and downloads the bounded QR
 * package. Match IDs stay only in local request state; no selection is written
 * into the route or browser URL.
 */
export function GameSheetPackageDialog({
  tournamentId,
  revision = 0,
}: {
  tournamentId: string;
  revision?: number;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [extraRows, setExtraRows] = useState<ScheduleItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [selected, setSelected] = useState<Map<string, ScheduleItem>>(
    new Map(),
  );
  const [busy, setBusy] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const busyRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const contextGeneration = useRef(0);
  const requestGeneration = useRef(0);

  const scheduleQuery = useQuery({
    queryKey: [
      "tournament-game-sheet-package",
      tournamentId,
      revision,
      appliedQuery,
    ],
    queryFn: () =>
      tournamentDayApi.getTournamentOperatorSchedule(tournamentId, {
        limit: 50,
        query: appliedQuery || undefined,
      }),
    enabled: open && Boolean(tournamentId),
    retry: false,
  });
  const schedule = scheduleQuery.data;
  const rows = useMemo(
    () => [...(schedule?.items ?? []), ...extraRows],
    [extraRows, schedule?.items],
  );
  const schedulePlanRevision = schedule?.schedule_plan_revision ?? null;

  useEffect(() => {
    contextGeneration.current += 1;
    loadingMoreRef.current = false;
    setLoadingMore(false);
    setExtraRows([]);
    setNextCursor(null);
    setSelected(new Map());
    setError(null);
  }, [appliedQuery, open, revision, schedulePlanRevision]);

  useEffect(() => {
    setNextCursor(schedule?.next_cursor ?? null);
  }, [schedule?.next_cursor]);

  const close = () => {
    contextGeneration.current += 1;
    requestGeneration.current += 1;
    busyRef.current = false;
    loadingMoreRef.current = false;
    setBusy(false);
    setLoadingMore(false);
    setOpen(false);
    setError(null);
    setSelected(new Map());
    setExtraRows([]);
    setNextCursor(null);
  };

  const toggle = (item: ScheduleItem) => {
    const key = matchKey(item);
    setSelected((current) => {
      const next = new Map(current);
      if (next.has(key)) {
        next.delete(key);
      } else if (next.size < 50) {
        next.set(key, item);
      }
      return next;
    });
  };

  const loadMore = async () => {
    if (
      !nextCursor ||
      schedulePlanRevision === null ||
      busyRef.current ||
      loadingMoreRef.current
    )
      return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const context = {
      cursor: nextCursor,
      generation: contextGeneration.current,
      query: appliedQuery,
      schedulePlanRevision,
    };
    setError(null);
    try {
      const next = await tournamentDayApi.getTournamentOperatorSchedule(
        tournamentId,
        {
          limit: 50,
          cursor: context.cursor,
          query: context.query || undefined,
        },
      );
      if (contextGeneration.current !== context.generation) return;
      if (next.schedule_plan_revision !== context.schedulePlanRevision) {
        setExtraRows([]);
        setSelected(new Map());
        setNextCursor(null);
        setError(
          "The schedule changed. Review the current matches before printing.",
        );
        return;
      }
      setExtraRows((current) => [...current, ...next.items]);
      setNextCursor(next.next_cursor ?? null);
    } catch {
      if (contextGeneration.current === context.generation) {
        setError("More scheduled matches could not be loaded.");
      }
    } finally {
      if (contextGeneration.current === context.generation) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  };

  const applyQuery = () => {
    contextGeneration.current += 1;
    loadingMoreRef.current = false;
    setLoadingMore(false);
    setAppliedQuery(query.trim());
  };

  const download = async () => {
    if (
      busyRef.current ||
      schedulePlanRevision === null ||
      selected.size === 0 ||
      selected.size > 50
    )
      return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    const generation = ++requestGeneration.current;
    try {
      const bundle = await tournamentApi.generateTournamentGameSheetPackage(
        tournamentId,
        {
          matches: [...selected.values()].map((item) => item.match),
          expected_schedule_plan_revision: schedulePlanRevision,
          idempotency_key: crypto.randomUUID(),
        },
      );
      if (generation !== requestGeneration.current) return;
      const href = URL.createObjectURL(bundle.blob);
      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.download = bundle.filename;
      anchor.rel = "noopener";
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(href);
      close();
    } catch {
      if (generation === requestGeneration.current) setError(errorMessage());
    } finally {
      if (generation === requestGeneration.current) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={() => {
          contextGeneration.current += 1;
          setOpen(true);
        }}
      >
        Prepare QR game sheets
      </Button>
      <Dialog
        open={open}
        onClose={busy ? undefined : close}
        fullWidth
        maxWidth="md"
        aria-labelledby="game-sheet-package-title"
      >
        <DialogTitle id="game-sheet-package-title">Game sheets</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <DialogContentText>
              Select up to 50 matches from the current published schedule.
              Reprint a match to revoke its earlier QR sheet.
            </DialogContentText>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                label="Find scheduled match"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    applyQuery();
                  }
                }}
                size="small"
                fullWidth
              />
              <Button
                size="small"
                variant="outlined"
                onClick={applyQuery}
              >
                Find
              </Button>
            </Stack>
            {scheduleQuery.isPending ? (
              <Alert severity="info">Loading scheduled matches.</Alert>
            ) : null}
            {scheduleQuery.isError ? (
              <Alert severity="error">
                Scheduled matches are unavailable. Try again.
              </Alert>
            ) : null}
            {schedule && schedulePlanRevision === null ? (
              <Alert severity="info">
                There is no current published schedule to print.
              </Alert>
            ) : null}
            {schedulePlanRevision !== null ? (
              <FormGroup aria-label="Current scheduled matches">
                {rows.map((item) => {
                  const key = matchKey(item);
                  return (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          checked={selected.has(key)}
                          disabled={!selected.has(key) && selected.size >= 50}
                          onChange={() => toggle(item)}
                        />
                      }
                      label={item.match_display_label}
                    />
                  );
                })}
              </FormGroup>
            ) : null}
            {schedule && rows.length === 0 && schedulePlanRevision !== null ? (
              <Alert severity="info">
                No scheduled matches match this filter.
              </Alert>
            ) : null}
            {nextCursor ? (
              <Button
                size="small"
                variant="text"
                disabled={loadingMore}
                onClick={() => void loadMore()}
              >
                Load more scheduled matches
              </Button>
            ) : null}
            {error ? <Alert severity="error">{error}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={
              busy || schedulePlanRevision === null || selected.size === 0
            }
            onClick={() => void download()}
          >
            {busy ? "Generating…" : "Generate game-sheet package"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
